import {HTMLAttributes, useEffect, useState} from 'react';
import {SubmitHandler, useForm} from 'react-hook-form';
import styled from 'styled-components';
import {getGqlErrorMessage} from '@/api/request';
import {useLogin, useRefetchMe} from '@/api/session';
import {useAirbridgeTracking, useGA4WithParams} from '@/hooks/tracking';
import {FlexCenter, FlexFullGrow} from '@/styled-components';
import {palette} from '@/styles';
import {encryptRSA} from '@/utils';
import {Button} from '../Button';
import {TextField} from '../TextField';
import {typographyStyle} from '../Typography';
import {InputContainer, TextFieldLabel} from './StyledComponents';

interface Inputs {
  email: string;
  password: string;
}

interface IProps extends HTMLAttributes<HTMLFormElement> {
  email: string;
  defaultValue?: string;
  updatePassword?(): void;
}

export const SignInPasswordStep = ({
  email,
  defaultValue,
  updatePassword,
  ...props
}: IProps) => {
  const {
    register,
    handleSubmit,
    setError,
    watch,
    setFocus,
    formState: {errors, touchedFields},
  } = useForm<Inputs>({mode: 'all'});

  const {mutate: signin, isPending} = useLogin();
  const refetchMe = useRefetchMe();

  const tracking = useGA4WithParams();
  const trackAirbridge = useAirbridgeTracking();

  const [value, setValue] = useState<string | undefined>(defaultValue);

  const validate = (value: string) => {
    if (touchedFields.password) {
      if (!value?.length) {
        return '비밀번호를 입력해 주세요.';
      }
    }
  };

  const onSubmit: SubmitHandler<Inputs> = async ({password, ...data}) => {
    const encryptedPwdBase64 = await encryptRSA(password);

    if (!encryptedPwdBase64) {
      setError('password', {
        type: 'encrypt-error',
        message:
          '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요. (encrypt)',
      });

      return;
    }

    signin(
      {
        ...data,
        password: encryptedPwdBase64,
      },
      {
        onSuccess: () => {
          tracking('login');
          trackAirbridge('airbridge.user.signin');
          refetchMe();
        },
        onError: (error) => {
          const isWrongPassword =
            getGqlErrorMessage(error, false) ===
            'Invalid username or password.';

          setError('password', {
            type: error?.name || 'server-error',
            message: isWrongPassword
              ? '비밀번호가 일치하지 않습니다.'
              : getGqlErrorMessage(
                  error,
                  '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요.'
                ),
          });
        },
      }
    );
  };

  useEffect(() => {
    setFocus('password');
  }, [setFocus]);

  useEffect(() => {
    const subscription = watch((value) => {
      setValue(value.password);
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch]);

  const error = errors['password']?.message;

  return (
    <InputContainer as="form" {...props} onSubmit={handleSubmit(onSubmit)}>
      <input {...register('email')} value={email} hidden />
      <TextFieldLabel error={!!error?.length}>비밀번호</TextFieldLabel>
      <TextField
        {...register('password', {
          validate,
          minLength: {
            value: 8,
            message: '8자 이상, 문자/숫자/기호 사용',
          },
        })}
        fullWidth
        hasMaskButton
        variant="xlarge"
        placeholder="비밀번호를 입력해 주세요."
        type="password"
        autoComplete="new-password"
        maxLength={100}
        value={value}
        error={error}
      />
      <FlexCenter>
        <ResetButton onClick={updatePassword}>
          비밀번호를 잊으셨나요?
        </ResetButton>
      </FlexCenter>
      <FlexFullGrow />
      <Button
        type="submit"
        disabled={error || !value || isPending}
        variant="fill"
        color="primary"
        size="mediumLarge"
        fullWidth
      >
        {isPending ? '로그인 중...' : '로그인'}
      </Button>
    </InputContainer>
  );
};

const ResetButton = styled.div`
  ${{...typographyStyle.medium14}}
  text-decoration: underline;
  cursor: pointer;
  color: ${palette.textPrimary};
  margin-top: 24px;
`;
